import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import posthog from 'posthog-js';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * React error boundary that catches unhandled render errors, reports them
 * to PostHog, and displays a graceful fallback UI.
 *
 * @example
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 *
 * @example
 * <ErrorBoundary fallback={<MyCustomFallback />}>
 *   <RiskyComponent />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  /**
   * Updates state so the next render shows the fallback UI.
   *
   * @param error - The error that was thrown
   * @returns New state indicating an error has occurred
   */
  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  /**
   * Logs the caught error to PostHog and the console.
   *
   * @param error - The error that was thrown
   * @param errorInfo - React component stack information
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Analytics: track error boundary triggered
    try {
      posthog.capture('error_boundary_triggered', {
        error_message: error.message,
        error_name: error.name,
        component_stack: errorInfo.componentStack?.slice(0, 500),
        route: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
      });
    } catch {
      // Analytics must never break the error boundary
    }

    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  /**
   * Renders the fallback UI when an error has been caught, or children otherwise.
   *
   * @returns The fallback UI or the children
   */
  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="max-w-md w-full mx-auto p-8 text-center">
            <h1 className="text-2xl font-semibold text-foreground mb-2">Something went wrong</h1>
            <p className="text-muted-foreground mb-6">
              An unexpected error occurred. Please refresh the page to try again.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center justify-center px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 font-medium text-sm transition-colors"
            >
              Refresh page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
